import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { LogOut, ShieldX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getRouteManifestEntry } from '@/config/routeManifest';
import { getAuthenticatedRoles } from '@/domain/auth/authNavigation';
import { resolveRouteAccess } from '@/domain/auth/routeAccess';
import { useAuth } from '@/hooks/useAuth';
import { isClientUser, isDialgiaStaff } from '@/lib/security/rbac';

interface RouteAccessGuardProps {
  children: ReactNode;
}

const FullPageStatus = () => (
  <main
    id="main-content"
    className="flex min-h-screen items-center justify-center bg-background p-6"
    aria-busy="true"
  >
    <div className="flex items-center gap-3 text-muted-foreground" role="status">
      <span
        className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"
        aria-hidden="true"
      />
      <span>Checking access...</span>
    </div>
  </main>
);

export const RouteAccessGuard = ({ children }: RouteAccessGuardProps) => {
  const location = useLocation();
  const { user, profile, loading, profileLoading, getDefaultRoute, hasAdminAccess, signOut } = useAuth();
  const access = getRouteManifestEntry(location.pathname)?.access ?? 'system';
  const roles = getAuthenticatedRoles(profile);
  const requiresProfile = access === 'admin' || access === 'client' || access === 'staff';
  const decision = resolveRouteAccess({
    access,
    authLoading: loading || (requiresProfile && Boolean(user) && profileLoading),
    authenticated: Boolean(user),
    admin: hasAdminAccess(),
    client: isClientUser(roles),
    staff: isDialgiaStaff(roles),
  });

  if (decision === 'loading') return <FullPageStatus />;

  if (decision === 'sign-in') {
    const destination = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(destination)}`} replace />;
  }

  if (decision === 'forbidden') {
    return (
      <main id="main-content" className="flex min-h-screen items-center justify-center bg-background p-6">
        <section className="w-full max-w-lg text-center" aria-labelledby="access-denied-title">
          <ShieldX className="mx-auto mb-5 h-12 w-12 text-destructive" aria-hidden="true" />
          <h1 id="access-denied-title" className="text-2xl font-semibold text-foreground">
            Access restricted
          </h1>
          <p className="mt-3 text-muted-foreground">
            Your account does not have permission to open this area.
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Button asChild>
              <Link to={getDefaultRoute()}>Go to your home</Link>
            </Button>
            <Button type="button" variant="outline" onClick={() => void signOut()}>
              <LogOut aria-hidden="true" />
              Sign out
            </Button>
          </div>
        </section>
      </main>
    );
  }

  return <>{children}</>;
};
